import React, { useEffect, useMemo, useState } from "react";
import "./NewsletterModule.css";
import {
  newsletterCancelCampaign,
  newsletterCreateCampaign,
  newsletterGetCampaign,
  newsletterGetHistory,
  newsletterListCampaigns,
  newsletterListSubscribers,
  newsletterPreviewCampaign,
  newsletterSendCampaign,
  newsletterScheduleCampaign,
  newsletterTestCampaign,
  newsletterUpdateCampaign,
} from "../adminApi";

const DEFAULT_BUILDER = {
  headerTitle: "Bhoomi BioTechnology",
  headerSubtitle: "Latest updates from Bhoomi BioTechnology",
  greeting: "Hello,",
  intro: "",
  paragraphs: [""],
  sectionHeading: "",
  bullets: [""],
  highlightTitle: "",
  highlightText: "",
  buttonLabel: "",
  buttonUrl: "",
  closing: "Regards,",
  signature: "Bhoomi BioTechnology",
  footerText: "You are receiving this email because you subscribed to Bhoomi BioTechnology updates.",
};

const EMPTY_EDITOR = {
  id: null,
  name: "",
  subject: "",
  preheader: "",
  html_content: "",
  text_content: "",
  status: "draft",
};

const STATUS_LABELS = {
  draft: "Draft",
  ready: "Ready",
  sending: "Sending",
  completed: "Completed",
  completed_with_errors: "Completed with errors",
  failed: "Failed",
  cancelled: "Cancelled",
  scheduled: "Scheduled",
};

const BUILDER_MARKER = "BHOOMI_NEWSLETTER_BUILDER_V1";

function campaignScheduledAtToInput(value) {
  if (!value) return "";
  return String(value).slice(0, 16).replace(" ", "T");
}

function scheduleInputToBackend(value) {
  if (!value) return "";
  return `${value.replace("T", " ")}:00`;
}

function getIndiaDateTimeLocal() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value])
  );

  return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`;
}

const SOCIAL_LINKS = [
  {
    name: "Instagram",
    href: "https://www.instagram.com/bhoomibiotechnology?igsh=MXhuaGE3NGk2MHdsaQ==",
    iconUrl: "https://www.bhoomibiotech.com/email-assets/social/instagram.png",
    alt: "Instagram",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/profile.php?id=61587262854642&sfnsn=wa",
    iconUrl: "https://www.bhoomibiotech.com/email-assets/social/facebook.png",
    alt: "Facebook",
  },
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/bhoomi-biotechnology-venture-for-research-and-dev/",
    iconUrl: "https://www.bhoomibiotech.com/email-assets/social/linkedin.png",
    alt: "LinkedIn",
  },
  {
    name: "X",
    href: "https://x.com/bhoomibiotechx?s=11",
    iconUrl: "https://www.bhoomibiotech.com/email-assets/social/x.png",
    alt: "X",
  },
];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value || 0));
}

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function escapeTextToHtml(value) {
  return escapeHtml(value).replace(/\r?\n/g, "<br>");
}

function safeUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw);
    if (url.protocol === "https:" || url.protocol === "http:") return url.href;
  } catch {
    return "";
  }
  return "";
}

function encodeBuilderState(state) {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function decodeBuilderState(html) {
  const source = String(html || "");
  const marker = `<!-- ${BUILDER_MARKER}:`;
  const start = source.indexOf(marker);
  if (start < 0) return null;
  const payloadStart = start + marker.length;
  const end = source.indexOf(" -->", payloadStart);
  if (end < 0) return null;

  try {
    const binary = atob(source.slice(payloadStart, end));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

function ensureBuilderState(state) {
  const safe = { ...DEFAULT_BUILDER, ...(state || {}) };
  safe.paragraphs = Array.isArray(safe.paragraphs) && safe.paragraphs.length ? safe.paragraphs : [""];
  safe.bullets = Array.isArray(safe.bullets) && safe.bullets.length ? safe.bullets : [""];
  return safe;
}

function buildNewsletterHtml(builder) {
  const state = ensureBuilderState(builder);
  const paragraphs = state.paragraphs
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((item) => `<p style="margin:0 0 16px;line-height:1.7;">${escapeTextToHtml(item)}</p>`)
    .join("");

  const bullets = state.bullets
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .map((item) => `<li style="margin:0 0 8px;line-height:1.55;">${escapeTextToHtml(item)}</li>`)
    .join("");

  const highlight = state.highlightText.trim()
    ? `<div style="margin:22px 0;padding:18px 20px;background:#eef7f1;border:1px solid #d7eadc;border-radius:10px;">
        ${state.highlightTitle.trim() ? `<strong style="display:block;margin-bottom:7px;color:#174a36;font-size:16px;">${escapeHtml(state.highlightTitle)}</strong>` : ""}
        <div style="color:#43564c;line-height:1.65;">${escapeTextToHtml(state.highlightText)}</div>
      </div>`
    : "";

  const ctaUrl = safeUrl(state.buttonUrl);
  const cta = state.buttonLabel.trim() && ctaUrl
    ? `<div style="margin:24px 0 8px;"><a href="${escapeHtml(ctaUrl)}" style="display:inline-block;padding:12px 18px;background:#2d6a4f;color:#fff;text-decoration:none;border-radius:8px;font-weight:700;">${escapeHtml(state.buttonLabel)}</a></div>`
    : "";

  const sectionHeading = state.sectionHeading.trim()
    ? `<h2 style="margin:24px 0 12px;color:#143f36;font-size:20px;">${escapeHtml(state.sectionHeading)}</h2>`
    : "";

  const intro = state.intro.trim()
    ? `<p style="margin:0 0 16px;line-height:1.7;">${escapeTextToHtml(state.intro)}</p>`
    : "";

  const greeting = state.greeting.trim()
    ? `<p style="margin:0 0 16px;line-height:1.7;">${escapeTextToHtml(state.greeting)}</p>`
    : "";

  const list = bullets
    ? `<ul style="margin:0 0 18px;padding-left:24px;">${bullets}</ul>`
    : "";

  const closing = state.closing.trim()
    ? `<p style="margin:24px 0 4px;line-height:1.7;">${escapeTextToHtml(state.closing)}</p>`
    : "";

  const signature = state.signature.trim()
    ? `<p style="margin:0;line-height:1.7;"><strong>${escapeTextToHtml(state.signature)}</strong></p>`
    : "";

  const footer = state.footerText.trim()
    ? escapeTextToHtml(state.footerText)
    : "You are receiving this email because you subscribed to Bhoomi BioTechnology updates.";

  const socialIcons = SOCIAL_LINKS.map((social) => `
    <a href="${escapeHtml(social.href)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(social.name)}" style="display:inline-block;margin:0 5px 0 0;text-decoration:none;vertical-align:middle;">
      <img src="${escapeHtml(social.iconUrl)}" width="34" height="34" alt="${escapeHtml(social.alt)}" style="display:block;width:34px;height:34px;border:0;outline:none;text-decoration:none;">
    </a>
  `).join("");

  const generated = `
<div style="font-family:Arial,sans-serif;color:#173b34;">
  <div style="padding:22px 24px;background:#0d4d46;color:#fff;border-radius:10px 10px 0 0;">
    <div style="font-size:23px;font-weight:800;">${escapeHtml(state.headerTitle || "Bhoomi BioTechnology")}</div>
    ${state.headerSubtitle.trim() ? `<div style="margin-top:6px;font-size:13px;color:rgba(255,255,255,.82);">${escapeTextToHtml(state.headerSubtitle)}</div>` : ""}
  </div>
  <div style="padding:28px 24px;background:#fff;border:1px solid #d9e8e1;border-top:0;border-radius:0 0 10px 10px;">
    ${greeting}
    ${intro}
    ${paragraphs}
    ${sectionHeading}
    ${list}
    ${highlight}
    ${cta}
    ${closing}
    ${signature}
  </div>
  <div style="margin-top:12px;padding:18px 24px;background:#f7fbf8;border:1px solid #e3ece7;border-radius:10px;color:#687a75;font-size:12px;line-height:1.6;text-align:center;">
    <div>${footer}</div>
    <div style="margin-top:14px;">${socialIcons}</div>
  </div>
</div>`;

  return `<!-- ${BUILDER_MARKER}:${encodeBuilderState(state)} -->${generated}`;
}

function buildNewsletterText(builder) {
  const state = ensureBuilderState(builder);
  const lines = [];

  if (state.headerTitle.trim()) lines.push(state.headerTitle.trim());
  if (state.headerSubtitle.trim()) lines.push(state.headerSubtitle.trim());
  lines.push("");
  if (state.greeting.trim()) lines.push(state.greeting.trim(), "");
  if (state.intro.trim()) lines.push(state.intro.trim(), "");
  state.paragraphs.forEach((item) => {
    if (String(item || "").trim()) lines.push(String(item).trim(), "");
  });
  if (state.sectionHeading.trim()) lines.push(state.sectionHeading.trim(), "");
  const bullets = state.bullets.map((item) => String(item || "").trim()).filter(Boolean);
  if (bullets.length) {
    bullets.forEach((item) => lines.push(`• ${item}`));
    lines.push("");
  }
  if (state.highlightTitle.trim()) lines.push(state.highlightTitle.trim());
  if (state.highlightText.trim()) lines.push(state.highlightText.trim(), "");
  if (state.buttonLabel.trim() && state.buttonUrl.trim()) lines.push(`${state.buttonLabel.trim()}: ${state.buttonUrl.trim()}`, "");
  if (state.closing.trim()) lines.push(state.closing.trim());
  if (state.signature.trim()) lines.push(state.signature.trim());
  lines.push("", state.footerText.trim() || "You are receiving this email because you subscribed to Bhoomi BioTechnology updates.");
  lines.push("Follow Bhoomi BioTechnology:");
  SOCIAL_LINKS.forEach((social) => lines.push(`${social.name}: ${social.href}`));
  return lines.join("\n");
}

function buildLocalPreview(editor, builder) {
  const html = buildNewsletterHtml(builder);
  const previewUnsubscribe = `<div style="max-width:680px;margin:12px auto 0;padding:16px 24px;background:#f7fbf8;border:1px solid #e3ece7;border-radius:10px;color:#687a75;font-size:12px;line-height:1.6;text-align:center;"><a href="#unsubscribe-preview#" style="color:#087f73;">Unsubscribe</a></div>`;
  return `<!doctype html><html><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(editor.subject || "Newsletter Preview")}</title></head><body style="margin:0;background:#f7fbf8;padding:24px 10px;font-family:Arial,sans-serif;">${html}${previewUnsubscribe}</body></html>`;
}

function StatusPill({ status }) {
  return <span className={`bb-news-status ${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function getCampaignDisplayStatus(campaign) {
  if (
    campaign?.scheduled_at &&
    (campaign.status === "draft" || campaign.status === "ready")
  ) {
    return "scheduled";
  }
  return campaign?.status || "draft";
}

function Toast({ message, type = "success" }) {
  if (!message) return null;
  return <div className={`bb-news-toast ${type}`}>{message}</div>;
}

export default function NewsletterModule({ user, csrfToken }) {
  const [section, setSection] = useState("overview");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ active: null, unsubscribed: null, campaigns: 0, sending: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPagination, setCampaignPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignStatus, setCampaignStatus] = useState("");
  const [editor, setEditor] = useState(null);
  const [builder, setBuilder] = useState(DEFAULT_BUILDER);
  const [legacyMode, setLegacyMode] = useState(false);
  const [legacyHtml, setLegacyHtml] = useState("");
  const [editorLoading, setEditorLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState(user?.email || "");
  const [scheduledAt, setScheduledAt] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberPagination, setSubscriberPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [subscriberStatus, setSubscriberStatus] = useState("active");
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const [subscriberLoading, setSubscriberLoading] = useState(false);

  const canCreate = user?.permissions?.includes("newsletter.create");
  const canEdit = user?.permissions?.includes("newsletter.edit");
  const canPreview = user?.permissions?.includes("newsletter.preview");
  const canTest = user?.permissions?.includes("newsletter.test");
  const canSend = user?.permissions?.includes("newsletter.send");
  const canSubscribers = user?.permissions?.includes("newsletter.subscribers");
  const canHistory = user?.permissions?.includes("newsletter.history");

  const notify = (message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3600);
  };

  async function loadCampaigns(page = 1) {
    setLoading(true);
    try {
      const data = await newsletterListCampaigns({ page, limit: 20, status: campaignStatus, search: campaignSearch });
      setCampaigns(data.items || []);
      setCampaignPagination(data.pagination || { page, limit: 20, total: 0, pages: 1 });
    } catch (error) {
      notify(error.message || "Unable to load campaigns.", "error");
    } finally {
      setLoading(false);
    }
  }

  async function loadSubscriberStats() {
    if (!canSubscribers) return;
    try {
      const [active, unsubscribed] = await Promise.all([
        newsletterListSubscribers({ page: 1, limit: 1, status: "active" }),
        newsletterListSubscribers({ page: 1, limit: 1, status: "unsubscribed" }),
      ]);
      setStats((current) => ({
        ...current,
        active: active.pagination?.total ?? 0,
        unsubscribed: unsubscribed.pagination?.total ?? 0,
      }));
    } catch {
      setStats((current) => ({ ...current, active: null, unsubscribed: null }));
    }
  }

  async function loadStats() {
    try {
      const data = await newsletterListCampaigns({ page: 1, limit: 50 });
      const items = data.items || [];
      setStats((current) => ({
        ...current,
        campaigns: data.pagination?.total ?? items.length,
        sending: items.filter((item) => item.status === "sending").length,
      }));
    } catch {
      // Campaign loader surfaces errors.
    }
    loadSubscriberStats();
  }

  async function loadSubscribers(page = 1) {
    if (!canSubscribers) return;
    setSubscriberLoading(true);
    try {
      const data = await newsletterListSubscribers({ page, limit: 25, status: subscriberStatus, search: subscriberSearch });
      setSubscribers(data.items || []);
      setSubscriberPagination(data.pagination || { page, limit: 25, total: 0, pages: 1 });
    } catch (error) {
      notify(error.message || "Unable to load subscribers.", "error");
    } finally {
      setSubscriberLoading(false);
    }
  }

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (section === "campaigns" || section === "overview") loadCampaigns(1);
  }, [section]);

  useEffect(() => {
    if (section === "subscribers") loadSubscribers(1);
  }, [section, subscriberStatus]);

  useEffect(() => {
    const hasSending = campaigns.some((campaign) => campaign.status === "sending");
    if (!hasSending) return undefined;
    const timer = window.setInterval(() => {
      loadCampaigns(campaignPagination.page || 1);
      loadStats();
    }, 15000);
    return () => window.clearInterval(timer);
  }, [campaigns, campaignPagination.page, campaignStatus, campaignSearch]);

  const recentCampaigns = useMemo(() => campaigns.slice(0, 5), [campaigns]);

  function openCreate() {
    setEditor({ ...EMPTY_EDITOR });
    setScheduledAt("");
    setBuilder({ ...DEFAULT_BUILDER, paragraphs: [""], bullets: [""] });
    setLegacyMode(false);
    setLegacyHtml("");
    setPreview(null);
    setSection("editor");
  }

  async function openEdit(id) {
    setEditorLoading(true);
    setPreview(null);
    try {
      const data = await newsletterGetCampaign(id);
      const campaign = data.campaign;
      const parsed = decodeBuilderState(campaign.html_content);
      setEditor(campaign);
      setScheduledAt(campaignScheduledAtToInput(campaign.scheduled_at));
      if (parsed) {
        setBuilder(ensureBuilderState(parsed));
        setLegacyMode(false);
        setLegacyHtml("");
      } else {
        setBuilder({ ...DEFAULT_BUILDER });
        setLegacyMode(true);
        setLegacyHtml(campaign.html_content || "");
      }
      setSection("editor");
    } catch (error) {
      notify(error.message || "Unable to open campaign.", "error");
    } finally {
      setEditorLoading(false);
    }
  }

  function campaignPayload() {
    const html = legacyMode ? legacyHtml : buildNewsletterHtml(builder);
    const text = legacyMode ? (editor?.text_content || "") : buildNewsletterText(builder);
    return {
      name: editor?.name || "",
      subject: editor?.subject || "",
      preheader: editor?.preheader || "",
      html_content: html,
      text_content: text,
    };
  }

  async function saveCampaign() {
    if (!editor) return null;
    setEditorLoading(true);
    try {
      const payload = campaignPayload();
      const data = editor.id
        ? await newsletterUpdateCampaign(editor.id, payload, csrfToken)
        : await newsletterCreateCampaign(payload, csrfToken);
      setEditor(data.campaign);
      setPreview(null);
      notify(editor.id ? "Campaign saved." : "Campaign draft created.");
      return data.campaign;
    } catch (error) {
      notify(error.message || "Could not save campaign.", "error");
      return null;
    } finally {
      setEditorLoading(false);
    }
  }

  async function handlePreview() {
    let campaign = editor;
    if (!campaign?.id) campaign = await saveCampaign();
    if (!campaign?.id) return;
    setPreviewLoading(true);
    try {
      const data = await newsletterPreviewCampaign(campaign.id);
      setPreview(data);
    } catch (error) {
      notify(error.message || "Unable to load preview.", "error");
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleTestEmail() {
    let campaign = editor;
    if (!campaign?.id) campaign = await saveCampaign();
    if (!campaign?.id) return;
    if (!testEmail.trim()) {
      notify("Enter a test email address.", "error");
      return;
    }
    try {
      await newsletterTestCampaign(campaign.id, testEmail.trim(), csrfToken);
      notify(`Test email sent to ${testEmail.trim()}.`);
    } catch (error) {
      notify(error.message || "Test email could not be sent.", "error");
    }
  }

  async function handleSend() {
    let campaign = editor;
    if (!campaign?.id) campaign = await saveCampaign();
    if (!campaign?.id) return;
    const activeCount = stats.active ?? "your active";
    const approved = window.confirm(`Send this newsletter to ${activeCount} active subscriber(s)?\n\nThis queues the campaign for delivery; actual email delivery is handled by the worker scheduler.`);
    if (!approved) return;
    try {
      const data = await newsletterSendCampaign(campaign.id, csrfToken);
      setEditor(data.campaign);
      notify(`Campaign queued for ${data.queued_recipients || 0} recipient(s).`);
      await loadCampaigns(1);
      await loadStats();
    } catch (error) {
      notify(error.message || "Campaign could not be queued.", "error");
    }
  }

  async function handleSchedule() {
    let campaign = editor;
    if (!scheduledAt) {
      notify("Select a future date and time.", "error");
      return;
    }


    if (!campaign?.id) campaign = await saveCampaign();
    if (!campaign?.id) return;

    const formattedSchedule = scheduleInputToBackend(scheduledAt);
    const approved = window.confirm(
      `Schedule this newsletter for ${scheduledAt.replace("T", " ")} IST?\n\nThe campaign will remain saved until the scheduler starts delivery.`
    );

    if (!approved) return;

    try {
      const data = await newsletterScheduleCampaign(
        campaign.id,
        formattedSchedule,
        csrfToken
      );

      setEditor(data.campaign);
      setScheduledAt(campaignScheduledAtToInput(data.campaign?.scheduled_at));
      notify("Campaign scheduled successfully.");
      await loadCampaigns(1);
      await loadStats();
    } catch (error) {
      notify(error.message || "Campaign could not be scheduled.", "error");
    }
  }

  async function handleCancel(id) {
    if (!window.confirm("Cancel this draft campaign?")) return;
    try {
      await newsletterCancelCampaign(id, csrfToken);
      notify("Campaign cancelled.");
      await loadCampaigns(campaignPagination.page || 1);
      await loadStats();
    } catch (error) {
      notify(error.message || "Campaign could not be cancelled.", "error");
    }
  }

  async function openDetails(id) {
    setDetailLoading(true);
    setDetail(null);
    try {
      const campaignData = await newsletterGetCampaign(id);
      let history = [];
      if (canHistory) {
        const historyData = await newsletterGetHistory(id);
        history = historyData.items || [];
      }
      setDetail({ campaign: campaignData.campaign, history });
    } catch (error) {
      notify(error.message || "Unable to load campaign details.", "error");
    } finally {
      setDetailLoading(false);
    }
  }

  function updateEditorField(field, value) {
    setEditor((current) => ({ ...current, [field]: value }));
    setPreview(null);
  }

  function updateBuilderField(field, value) {
    setBuilder((current) => ({ ...current, [field]: value }));
    setPreview(null);
  }

  function updateParagraph(index, value) {
    setBuilder((current) => ({
      ...current,
      paragraphs: current.paragraphs.map((item, itemIndex) => itemIndex === index ? value : item),
    }));
    setPreview(null);
  }

  function addParagraph() {
    setBuilder((current) => ({ ...current, paragraphs: [...current.paragraphs, ""] }));
  }

  function removeParagraph(index) {
    setBuilder((current) => ({
      ...current,
      paragraphs: current.paragraphs.length === 1 ? [""] : current.paragraphs.filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  function updateBullet(index, value) {
    setBuilder((current) => ({
      ...current,
      bullets: current.bullets.map((item, itemIndex) => itemIndex === index ? value : item),
    }));
    setPreview(null);
  }

  function addBullet() {
    setBuilder((current) => ({ ...current, bullets: [...current.bullets, ""] }));
  }

  function removeBullet(index) {
    setBuilder((current) => ({
      ...current,
      bullets: current.bullets.length === 1 ? [""] : current.bullets.filter((_, itemIndex) => itemIndex !== index),
    }));
  }

  function convertLegacyToBuilder() {
    setBuilder({
      ...DEFAULT_BUILDER,
      paragraphs: [""],
      bullets: [""],
    });
    setLegacyMode(false);
    setLegacyHtml("");
    setPreview(null);
    notify("Simple newsletter editor enabled. Enter the content into the sections below.");
  }

  const topTitle = {
    overview: "Newsletter Overview",
    campaigns: "Campaigns",
    subscribers: "Subscribers",
    editor: editor?.id ? "Edit Campaign" : "Create Campaign",
  }[section];

  return (
    <section className="bb-news-module">
      <Toast message={toast?.message} type={toast?.type} />

      {section !== "editor" && (
        <>
          <div className="bb-news-hero">
            <div>
              <span className="bb-news-eyebrow">NEWSLETTER CONTROL</span>
              <h2>{topTitle}</h2>
              <p>Create, preview, test and deliver newsletters while keeping a complete campaign history.</p>
            </div>
            <div className="bb-news-hero-actions">
              {canCreate && <button className="bb-news-primary" onClick={openCreate}>+ New campaign</button>}
              <button className="bb-news-secondary" onClick={() => { loadCampaigns(1); loadStats(); }}>Refresh</button>
            </div>
          </div>

          <div className="bb-news-tabs" role="tablist">
            <button className={section === "overview" ? "active" : ""} onClick={() => setSection("overview")}>Overview</button>
            <button className={section === "campaigns" ? "active" : ""} onClick={() => setSection("campaigns")}>Campaigns</button>
            {canSubscribers && <button className={section === "subscribers" ? "active" : ""} onClick={() => setSection("subscribers")}>Subscribers</button>}
          </div>
        </>
      )}

      {section === "overview" && (
        <div className="bb-news-page">
          <div className="bb-news-stat-grid">
            <div className="bb-news-stat-card"><span>ACTIVE SUBSCRIBERS</span><strong>{stats.active === null ? "—" : formatNumber(stats.active)}</strong><small>Eligible for normal delivery</small></div>
            <div className="bb-news-stat-card"><span>UNSUBSCRIBED</span><strong>{stats.unsubscribed === null ? "—" : formatNumber(stats.unsubscribed)}</strong><small>Excluded from normal sends</small></div>
            <div className="bb-news-stat-card"><span>CAMPAIGNS</span><strong>{formatNumber(stats.campaigns)}</strong><small>All campaign records</small></div>
            <div className="bb-news-stat-card"><span>IN PROGRESS</span><strong>{formatNumber(stats.sending)}</strong><small>Waiting for worker delivery</small></div>
          </div>

          <div className="bb-news-section-title"><div><h3>Recent campaigns</h3><p>Latest newsletter activity.</p></div><button className="bb-news-text-btn" onClick={() => setSection("campaigns")}>View all →</button></div>
          {loading ? <div className="bb-news-panel bb-news-loading-row">Loading campaigns…</div> : recentCampaigns.length === 0 ? <div className="bb-news-panel bb-news-empty">No campaigns yet. Create your first draft to get started.</div> : <CampaignTable campaigns={recentCampaigns} onEdit={openEdit} onDetails={openDetails} onCancel={handleCancel} canEdit={canEdit} />}
        </div>
      )}

      {section === "campaigns" && (
        <div className="bb-news-page">
          <div className="bb-news-toolbar">
            <input value={campaignSearch} onChange={(event) => setCampaignSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && loadCampaigns(1)} placeholder="Search campaign or subject" />
            <select value={campaignStatus} onChange={(event) => { setCampaignStatus(event.target.value); window.setTimeout(() => loadCampaigns(1), 0); }}>
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="sending">Sending</option>
              <option value="completed">Completed</option>
              <option value="completed_with_errors">Completed with errors</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="bb-news-secondary" onClick={() => loadCampaigns(1)}>Search</button>
          </div>
          <CampaignTable campaigns={campaigns} onEdit={openEdit} onDetails={openDetails} onCancel={handleCancel} canEdit={canEdit} loading={loading} />
          <Pagination pagination={campaignPagination} onPage={loadCampaigns} />
        </div>
      )}

      {section === "subscribers" && (
        <div className="bb-news-page">
          <div className="bb-news-toolbar">
            <input value={subscriberSearch} onChange={(event) => setSubscriberSearch(event.target.value)} onKeyDown={(event) => event.key === "Enter" && loadSubscribers(1)} placeholder="Search subscriber email" />
            <select value={subscriberStatus} onChange={(event) => setSubscriberStatus(event.target.value)}>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="all">All</option>
            </select>
            <button className="bb-news-secondary" onClick={() => loadSubscribers(1)}>Search</button>
          </div>
          <div className="bb-news-panel">
            <div className="bb-news-table-wrap">
              <table className="bb-news-table">
                <thead><tr><th>Email</th><th>Status</th><th>Subscribed</th><th>Updated</th></tr></thead>
                <tbody>
                  {subscriberLoading ? <tr><td colSpan="4" className="bb-news-table-empty">Loading subscribers…</td></tr> : subscribers.length === 0 ? <tr><td colSpan="4" className="bb-news-table-empty">No subscribers found.</td></tr> : subscribers.map((subscriber) => <tr key={subscriber.id}><td><strong>{subscriber.email}</strong></td><td><StatusPill status={subscriber.status === "active" ? "completed" : "cancelled"} /><span className="bb-news-subscriber-status">{subscriber.status}</span></td><td>{formatDate(subscriber.subscribed_at || subscriber.created_at)}</td><td>{formatDate(subscriber.updated_at)}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination pagination={subscriberPagination} onPage={loadSubscribers} />
        </div>
      )}

      {section === "editor" && editor && (
        <div className="bb-news-editor-page">
          <div className="bb-news-editor-head">
            <button className="bb-news-back" onClick={() => { setSection("campaigns"); setPreview(null); }}>← Campaigns</button>
            <div><span className="bb-news-eyebrow">{editor.id ? `CAMPAIGN #${editor.id}` : "NEW DRAFT"}</span><h2>{editor.name || "Untitled campaign"}</h2></div>
            <StatusPill status={editor.status || "draft"} />
          </div>

          <div className="bb-news-editor-grid">
            <div className="bb-news-panel bb-news-editor-form">
              <div className="bb-news-builder-intro">
                <div>
                  <span className="bb-news-builder-badge">EASY NEWSLETTER BUILDER</span>
                  <h3>Write content. We handle the HTML.</h3>
                  <p>Fill in the sections below. You do not need to type HTML tags, links, paragraph tags or email footer code.</p>
                </div>
              </div>

              <div className="bb-news-builder-section">
                <div className="bb-news-builder-section-head"><div><span>STEP 1</span><h4>Campaign details</h4></div></div>
                <div className="bb-news-field-row">
                  <label><span>Campaign name</span><input value={editor.name || ""} onChange={(event) => updateEditorField("name", event.target.value)} placeholder="e.g. September Newsletter" disabled={!canEdit && !!editor.id} /></label>
                </div>
                <div className="bb-news-field-row two">
                  <label><span>Email subject</span><input value={editor.subject || ""} onChange={(event) => updateEditorField("subject", event.target.value)} placeholder="Subject shown in the inbox" disabled={!canEdit && !!editor.id} /></label>
                  <label><span>Preheader <em>optional</em></span><input value={editor.preheader || ""} onChange={(event) => updateEditorField("preheader", event.target.value)} placeholder="Short preview text" disabled={!canEdit && !!editor.id} /></label>
                </div>
              </div>

              {legacyMode ? (
                <div className="bb-news-legacy-box">
                  <div className="bb-news-builder-section-head"><div><span>LEGACY CAMPAIGN</span><h4>Existing HTML content</h4></div></div>
                  <p>This campaign was created before the easy editor. Keep the existing HTML or convert it to the new section-based editor.</p>
                  <textarea value={legacyHtml} onChange={(event) => { setLegacyHtml(event.target.value); setPreview(null); }} spellCheck="false" />
                  <button className="bb-news-secondary" onClick={convertLegacyToBuilder}>Convert to easy editor</button>
                </div>
              ) : (
                <>
                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 2</span><h4>Email header</h4><p>What readers see at the top of the email.</p></div></div>
                    <div className="bb-news-field-row two">
                      <label><span>Header title</span><input value={builder.headerTitle} onChange={(event) => updateBuilderField("headerTitle", event.target.value)} placeholder="Bhoomi BioTechnology" /></label>
                      <label><span>Header subtitle <em>optional</em></span><input value={builder.headerSubtitle} onChange={(event) => updateBuilderField("headerSubtitle", event.target.value)} placeholder="Latest updates and announcements" /></label>
                    </div>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 3</span><h4>Greeting & introduction</h4><p>Start the newsletter naturally.</p></div></div>
                    <div className="bb-news-field-row">
                      <label><span>Greeting</span><input value={builder.greeting} onChange={(event) => updateBuilderField("greeting", event.target.value)} placeholder="Hello," /></label>
                      <label><span>Introduction <em>optional</em></span><textarea className="bb-news-compact-textarea" value={builder.intro} onChange={(event) => updateBuilderField("intro", event.target.value)} placeholder="A short introduction to this newsletter..." /></label>
                    </div>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 4</span><h4>Main paragraphs</h4><p>Add as many paragraphs as you need. The system automatically formats them.</p></div></div>
                    {builder.paragraphs.map((paragraph, index) => (
                      <div className="bb-news-repeat-row" key={`paragraph-${index}`}>
                        <label><span>Paragraph {index + 1}</span><textarea value={paragraph} onChange={(event) => updateParagraph(index, event.target.value)} placeholder="Write your paragraph here..." /></label>
                        <button className="bb-news-remove-btn" type="button" onClick={() => removeParagraph(index)} aria-label={`Remove paragraph ${index + 1}`}>Remove</button>
                      </div>
                    ))}
                    <button className="bb-news-add-btn" type="button" onClick={addParagraph}>+ Add another paragraph</button>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 5</span><h4>Subheading <em>optional</em></h4><p>Use this to introduce a new topic or section.</p></div></div>
                    <label><span>Section heading</span><input value={builder.sectionHeading} onChange={(event) => updateBuilderField("sectionHeading", event.target.value)} placeholder="What's new this month?" /></label>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 6</span><h4>Bullet points <em>optional</em></h4><p>Useful for announcements, features, key points or updates.</p></div></div>
                    {builder.bullets.map((bullet, index) => (
                      <div className="bb-news-repeat-row" key={`bullet-${index}`}>
                        <label><span>Bullet {index + 1}</span><input value={bullet} onChange={(event) => updateBullet(index, event.target.value)} placeholder="Write a key point..." /></label>
                        <button className="bb-news-remove-btn" type="button" onClick={() => removeBullet(index)} aria-label={`Remove bullet ${index + 1}`}>Remove</button>
                      </div>
                    ))}
                    <button className="bb-news-add-btn" type="button" onClick={addBullet}>+ Add another bullet</button>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 7</span><h4>Highlighted message <em>optional</em></h4><p>Add a visually highlighted announcement or important note.</p></div></div>
                    <div className="bb-news-field-row two">
                      <label><span>Highlight title</span><input value={builder.highlightTitle} onChange={(event) => updateBuilderField("highlightTitle", event.target.value)} placeholder="Important update" /></label>
                      <label><span>Highlight message</span><textarea className="bb-news-compact-textarea" value={builder.highlightText} onChange={(event) => updateBuilderField("highlightText", event.target.value)} placeholder="Write the important message..." /></label>
                    </div>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 8</span><h4>Call-to-action button <em>optional</em></h4><p>Use a button when you want readers to visit a page.</p></div></div>
                    <div className="bb-news-field-row two">
                      <label><span>Button text</span><input value={builder.buttonLabel} onChange={(event) => updateBuilderField("buttonLabel", event.target.value)} placeholder="Visit our website" /></label>
                      <label><span>Button link</span><input value={builder.buttonUrl} onChange={(event) => updateBuilderField("buttonUrl", event.target.value)} placeholder="https://www.example.com" /></label>
                    </div>
                    {builder.buttonLabel && builder.buttonUrl && !safeUrl(builder.buttonUrl) && <div className="bb-news-field-help error">Please enter a valid http:// or https:// link.</div>}
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 9</span><h4>Closing & signature</h4><p>The unsubscribe link is added automatically by the system. You do not need to write it.</p></div></div>
                    <div className="bb-news-field-row two">
                      <label><span>Closing</span><input value={builder.closing} onChange={(event) => updateBuilderField("closing", event.target.value)} placeholder="Regards," /></label>
                      <label><span>Signature</span><input value={builder.signature} onChange={(event) => updateBuilderField("signature", event.target.value)} placeholder="Bhoomi BioTechnology" /></label>
                    </div>
                  </div>

                  <div className="bb-news-builder-section">
                    <div className="bb-news-builder-section-head"><div><span>STEP 10</span><h4>Email footer & social media</h4><p>Write your footer message. Social media icons are added automatically; the unsubscribe link is handled separately by the system.</p></div></div>
                    <label><span>Footer message</span><textarea className="bb-news-compact-textarea" value={builder.footerText} onChange={(event) => updateBuilderField("footerText", event.target.value)} placeholder="You are receiving this email because you subscribed to Bhoomi BioTechnology updates." /></label>
                    <div className="bb-news-social-defaults">
                      <strong>Social media footer — added automatically</strong>
                      <span>Instagram · Facebook · LinkedIn · X</span>
                    </div>
                  </div>
                </>
              )}

              <div className="bb-news-action-bar">
                {canEdit && (editor.status === "draft" || editor.status === "ready") && <button className="bb-news-secondary" onClick={saveCampaign} disabled={editorLoading}>{editorLoading ? "Saving…" : "Save draft"}</button>}
                {canPreview && editor.id && <button className="bb-news-secondary" onClick={handlePreview} disabled={previewLoading}>{previewLoading ? "Loading preview…" : "Preview"}</button>}
                {canPreview && !editor.id && <button className="bb-news-secondary" onClick={handlePreview} disabled={previewLoading}>{previewLoading ? "Saving…" : "Save & preview"}</button>}
                {canTest && editor.id && editor.status !== "sending" && editor.status !== "completed" && editor.status !== "completed_with_errors" && <div className="bb-news-test-group"><input value={testEmail} onChange={(event) => setTestEmail(event.target.value)} type="email" placeholder="test@example.com" /><button className="bb-news-secondary" onClick={handleTestEmail}>Send test</button></div>}
                {canSend && (editor.status === "draft" || editor.status === "ready") && (
  <div className="bb-news-schedule-group">
    <label>
      <span>Schedule for <em>IST</em></span>
      <input
        type="datetime-local"
        value={scheduledAt}
        min={getIndiaDateTimeLocal()}
        onChange={(event) => setScheduledAt(event.target.value)}
        disabled={editorLoading}
      />
    </label>
    <button
      className="bb-news-secondary"
      onClick={handleSchedule}
      disabled={editorLoading || !scheduledAt}
    >
      Schedule newsletter
    </button>
  </div>
)}
{canSend && editor.id && (editor.status === "draft" || editor.status === "ready") && <button className="bb-news-primary" onClick={handleSend}>Send newsletter</button>}
                {canEdit && editor.id && (editor.status === "draft" || editor.status === "ready") && <button className="bb-news-danger" onClick={() => handleCancel(editor.id)}>Cancel</button>}
              </div>
            </div>

            <div className="bb-news-panel bb-news-preview-panel">
              <div className="bb-news-panel-head"><div><h3>Live preview</h3><p>{preview ? "Rendered by the Newsletter API." : "Your newsletter layout updates from the sections on the left."}</p></div></div>
              {previewLoading ? <div className="bb-news-preview-empty">Loading preview…</div> : preview?.preview_html ? <iframe title="Newsletter preview" className="bb-news-preview-frame" sandbox="" srcDoc={preview.preview_html} /> : <iframe title="Newsletter live preview" className="bb-news-preview-frame" sandbox="" srcDoc={buildLocalPreview(editor, builder)} />}
              {editor.status === "sending" && <div className="bb-news-scheduler-note"><strong>Queued for delivery</strong><span>The campaign is waiting for the worker scheduler to process its recipient queue.</span></div>}
            </div>
          </div>
        </div>
      )}

      {detail && <CampaignDetail detail={detail} onClose={() => setDetail(null)} />}
      {detailLoading && <div className="bb-news-modal-overlay"><div className="bb-news-modal-card">Loading campaign details…</div></div>}
    </section>
  );
}

function CampaignTable({ campaigns, onEdit, onDetails, onCancel, canEdit, loading = false }) {
  return (
    <div className="bb-news-panel">
      <div className="bb-news-table-wrap">
        <table className="bb-news-table">
          <thead><tr><th>Campaign</th><th>Status</th><th>Recipients</th><th>Delivery</th><th>Updated</th><th /></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan="6" className="bb-news-table-empty">Loading campaigns…</td></tr> : campaigns.length === 0 ? <tr><td colSpan="6" className="bb-news-table-empty">No campaigns found.</td></tr> : campaigns.map((campaign) => {
              const processed = (campaign.sent_count || 0) + (campaign.failed_count || 0) + (campaign.skipped_count || 0);
              return <tr key={campaign.id}>
                <td><button className="bb-news-campaign-name" onClick={() => onDetails(campaign.id)}><strong>{campaign.name}</strong><span>{campaign.subject}</span></button></td>
                <td><StatusPill status={getCampaignDisplayStatus(campaign)} /></td>
                <td>{formatNumber(campaign.total_recipients)}</td>
                <td><strong>{formatNumber(processed)}</strong><span className="bb-news-muted-inline"> / {formatNumber(campaign.total_recipients)}</span></td>
                <td>{formatDate(campaign.updated_at)}</td>
                <td><div className="bb-news-row-actions">
                  {(campaign.status === "draft" || campaign.status === "ready") && canEdit && <button onClick={() => onEdit(campaign.id)}>Edit</button>}
                  <button onClick={() => onDetails(campaign.id)}>View</button>
                  {(campaign.status === "draft" || campaign.status === "ready") && canEdit && <button className="danger" onClick={() => onCancel(campaign.id)}>Cancel</button>}
                </div></td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CampaignDetail({ detail, onClose }) {
  const { campaign, history } = detail;
  return <div className="bb-news-modal-overlay" onMouseDown={onClose}>
    <div className="bb-news-detail-drawer" onMouseDown={(event) => event.stopPropagation()}>
      <div className="bb-news-detail-head"><div><span className="bb-news-eyebrow">CAMPAIGN #{campaign.id}</span><h2>{campaign.name}</h2><p>{campaign.subject}</p></div><button onClick={onClose}>×</button></div>
      <div className="bb-news-detail-grid">
        <div><span>STATUS</span><strong><StatusPill status={getCampaignDisplayStatus(campaign)} /></strong></div>
        <div><span>RECIPIENTS</span><strong>{formatNumber(campaign.total_recipients)}</strong></div>
        <div><span>SENT</span><strong>{formatNumber(campaign.sent_count)}</strong></div>
        <div><span>FAILED</span><strong>{formatNumber(campaign.failed_count)}</strong></div>
        <div><span>SKIPPED</span><strong>{formatNumber(campaign.skipped_count)}</strong></div>
        <div><span>QUEUED</span><strong>{formatNumber(campaign.queued_count)}</strong></div>
      </div>
      <div className="bb-news-section-title"><div><h3>Campaign timeline</h3><p>Administrative and delivery events.</p></div></div>
      <div className="bb-news-timeline">{history.length === 0 ? <div className="bb-news-empty">No history recorded.</div> : history.map((event) => <div className="bb-news-event" key={event.id}><div className="bb-news-event-dot" /><div><strong>{event.event_type.replaceAll("_", " ")}</strong><p>{event.message}</p><small>{formatDate(event.created_at)}</small></div></div>)}</div>
    </div>
  </div>;
}

function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.pages <= 1) return null;
  return <div className="bb-news-pagination"><button disabled={pagination.page <= 1} onClick={() => onPage(pagination.page - 1)}>← Previous</button><span>Page {pagination.page} of {pagination.pages}</span><button disabled={pagination.page >= pagination.pages} onClick={() => onPage(pagination.page + 1)}>Next →</button></div>;
}